export const DEFAULT_THESIS_INTEGRITY_WEIGHTS = Object.freeze({
  Q_s: 0.25,
  O: 0.25,
  D: 0.15,
  H: 0.15,
  X: 0.10,
  R: 0.10,
});

export const OVERLAP_DIMENSIONS = [
  { key: 'o_address', label: 'Address', tooltip: 'Shared wallet and contract addresses across supporting stories.' },
  { key: 'o_token', label: 'Token', tooltip: 'Shared token references and asset identities.' },
  { key: 'o_contract', label: 'Contract', tooltip: 'Shared contracts, pools, routers, or related execution surfaces.' },
  { key: 'o_venue', label: 'Venue', tooltip: 'Shared exchanges, pools, or venues where the activity shows up.' },
  { key: 'o_time', label: 'Time', tooltip: 'How tightly the supporting stories cluster in time.' },
  { key: 'o_motif', label: 'Motif', tooltip: 'Shared behavioral pattern or story motif across the evidence set.' },
];

export const THESIS_COMPONENTS = [
  { key: 'Q_s', label: 'Q_S', sign: '+', rowKey: 'q_s' },
  { key: 'O', label: 'O', sign: '+', rowKey: 'o_total' },
  { key: 'D', label: 'D', sign: '+', rowKey: 'diversity' },
  { key: 'H', label: 'H', sign: '+', rowKey: 'history_strength' },
  { key: 'X', label: 'X', sign: '+', rowKey: 'support_strength' },
  { key: 'R', label: 'R', sign: '-', rowKey: 'risk_pressure' },
];

function round4(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 10000) / 10000;
}

function clamp01(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function normalizeWeights(weights) {
  const merged = {
    ...DEFAULT_THESIS_INTEGRITY_WEIGHTS,
    ...(weights && typeof weights === 'object' ? weights : {}),
  };
  const total = Object.values(merged).reduce((acc, value) => {
    const n = Number(value);
    return acc + (Number.isFinite(n) && n >= 0 ? n : 0);
  }, 0);
  if (!(total > 0)) return { ...DEFAULT_THESIS_INTEGRITY_WEIGHTS };
  return Object.keys(DEFAULT_THESIS_INTEGRITY_WEIGHTS).reduce((acc, key) => {
    acc[key] = round4(Number(merged[key]) / total);
    return acc;
  }, {});
}

export function getOverlapVector(thesis) {
  return OVERLAP_DIMENSIONS.map((dimension) => ({
    ...dimension,
    value: clamp01(thesis && thesis[dimension.key]),
  }));
}

export function getThesisIntegrityData(thesis) {
  if (!thesis || typeof thesis !== 'object') return null;

  const overlap = getOverlapVector(thesis);
  const weights = normalizeWeights();
  const q_s = clamp01(thesis.q_s != null ? thesis.q_s : thesis.c_thesis_base);
  const history_strength = clamp01(thesis.history_strength != null ? thesis.history_strength : thesis.conviction);
  const support_strength = clamp01(thesis.support_strength != null ? thesis.support_strength : Math.log1p(Number(thesis.n_supporting || 0)) / Math.log1p(8));
  const risk_pressure = clamp01(thesis.risk_pressure != null ? thesis.risk_pressure : (Number(thesis.fraud_risk || 0) / 100));

  const components = [
    { key: 'Q_s', label: 'Q_S', sign: '+', value: q_s, weight: weights.Q_s },
    { key: 'O', label: 'O', sign: '+', value: clamp01(thesis.o_total), weight: weights.O },
    { key: 'D', label: 'D', sign: '+', value: clamp01(thesis.diversity), weight: weights.D },
    { key: 'H', label: 'H', sign: '+', value: history_strength, weight: weights.H },
    { key: 'X', label: 'X', sign: '+', value: support_strength, weight: weights.X },
    { key: 'R', label: 'R', sign: '-', value: risk_pressure, weight: weights.R },
  ].map((item) => ({
    ...item,
    contribution: round4(item.weight * item.value * (item.sign === '-' ? -1 : 1)),
  }));

  return {
    overlap,
    weights,
    components,
    c_thesis: clamp01(thesis.c_thesis),
    c_thesis_base: clamp01(thesis.c_thesis_base),
    f_theta: clamp01(thesis.f_theta),
    c_thesis_enhanced: clamp01(thesis.c_thesis_enhanced != null ? thesis.c_thesis_enhanced : thesis.c_thesis),
  };
}
